/**
 * BrokenLinksChecker \u2014 scans navigation + content for broken links.
 *
 * \u2022 Scans custom nav items for internal paths that don't match known routes
 * \u2022 Scans content overrides for URLs (http/https)
 * \u2022 Checks external URLs via server proxy endpoint to avoid CORS
 * \u2022 Reports results grouped by type (internal broken, external broken)
 */

import { useState, useCallback } from "react";
import {
  Link2Off,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  X,
  RefreshCw,
  Globe,
  FileQuestion,
} from "lucide-react";
import { apiFetch } from "../../utils/apiClient";
import { useContentEditingSafe } from "./ContentEditingContext";
import { useTemplateId, useTemplateType } from "./TemplateContext";

const btnReset =
  "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

/* Known internal routes for each template type */
const KNOWN_BRANDING_PATHS = new Set([
  "/", "/about", "/services", "/service-a", "/service-b",
  "/products", "/product-a", "/product-b", "/projects",
  "/consulting", "/blog", "/contact", "/seo-manager",
  "/change-history", "/activity-log", "/business-info", "/site-tree",
]);

const KNOWN_STORE_PATHS = new Set([
  "/", "/about", "/catalog", "/blog", "/contact",
  "/seo-manager", "/change-history", "/activity-log",
  "/business-info", "/site-tree",
]);

export interface LinkCheckResult {
  url: string;
  source: string; // where the link was found (nav item, content key, etc.)
  type: "internal" | "external";
  status: "ok" | "broken" | "error" | "timeout";
  statusCode?: number;
  error?: string;
}

/** Extract URLs from a text string */
function extractUrls(text: string): string[] {
  const urlRegex = /https?:\/\/[^\s"'<>\]\)]+/gi;
  return Array.from(text.matchAll(urlRegex)).map((m) => m[0]);
}

/** Check if an internal path is valid */
function isValidInternalPath(path: string, templateType: "branding" | "store"): boolean {
  const cleanPath = path.replace(/\/$/, "") || "/";
  const knownPaths = templateType === "store" ? KNOWN_STORE_PATHS : KNOWN_BRANDING_PATHS;

  // Direct match
  if (knownPaths.has(cleanPath)) return true;

  // Dynamic routes: blog/:slug, catalog/:category, catalog/:category/:product
  if (cleanPath.startsWith("/blog/")) return true;
  if (cleanPath.startsWith("/catalog/")) return true;

  return false;
}

/** Check an external URL via server proxy */
async function checkExternalUrl(url: string): Promise<{ ok: boolean; statusCode?: number; error?: string }> {
  try {
    const data = await apiFetch("/link-check", {
      method: "POST",
      body: JSON.stringify({ url }),
    });
    if (data.ok) return { ok: true, statusCode: data.statusCode };
    return { ok: false, statusCode: data.statusCode, error: data.error };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

export function BrokenLinksChecker({ onClose }: { onClose: () => void }) {
  const siteId = useTemplateId();
  const templateType = useTemplateType();
  const editCtx = useContentEditingSafe();

  const [isScanning, setIsScanning] = useState(false);
  const [results, setResults] = useState<LinkCheckResult[]>([]);
  const [scanComplete, setScanComplete] = useState(false);
  const [progress, setProgress] = useState({ checked: 0, total: 0 });

  const runScan = useCallback(async () => {
    if (!editCtx) return;
    setIsScanning(true);
    setScanComplete(false);
    setResults([]);
    setProgress({ checked: 0, total: 0 });

    const linksToCheck: { url: string; source: string; type: "internal" | "external" }[] = [];

    // 1. Gather nav items from content overrides
    const overrides = editCtx.getAllOverrides();

    // Check custom nav items
    const navItemsRaw = overrides["__nav:custom-items"];
    if (navItemsRaw) {
      try {
        const navItems = JSON.parse(navItemsRaw);
        if (Array.isArray(navItems)) {
          for (const item of navItems) {
            if (item.path) {
              if (item.path.startsWith("http")) {
                linksToCheck.push({ url: item.path, source: `\u05E0\u05D9\u05D5\u05D5\u05D8: ${item.label || item.path}`, type: "external" });
              } else {
                linksToCheck.push({ url: item.path, source: `\u05E0\u05D9\u05D5\u05D5\u05D8: ${item.label || item.path}`, type: "internal" });
              }
            }
          }
        }
      } catch { /* ignore parse errors */ }
    }

    // 2. Scan all content overrides for URLs
    for (const [key, value] of Object.entries(overrides)) {
      if (typeof value !== "string") continue;
      if (key.startsWith("__")) continue; // skip meta keys

      const urls = extractUrls(value);
      for (const url of urls) {
        linksToCheck.push({
          url,
          source: `\u05EA\u05D5\u05DB\u05DF: ${key}`,
          type: "external",
        });
      }
    }

    // Deduplicate by URL
    const seen = new Set<string>();
    const uniqueLinks = linksToCheck.filter((l) => {
      if (seen.has(l.url)) return false;
      seen.add(l.url);
      return true;
    });

    setProgress({ checked: 0, total: uniqueLinks.length });

    const allResults: LinkCheckResult[] = [];

    // 3. Check internal links
    const internalLinks = uniqueLinks.filter((l) => l.type === "internal");
    for (const link of internalLinks) {
      const isValid = isValidInternalPath(link.url, templateType);
      allResults.push({
        url: link.url,
        source: link.source,
        type: "internal",
        status: isValid ? "ok" : "broken",
      });
      setProgress((p) => ({ ...p, checked: p.checked + 1 }));
    }

    // 4. Check external links (in batches of 3 to avoid overloading)
    const externalLinks = uniqueLinks.filter((l) => l.type === "external");
    const BATCH_SIZE = 3;
    for (let i = 0; i < externalLinks.length; i += BATCH_SIZE) {
      const batch = externalLinks.slice(i, i + BATCH_SIZE);
      const checks = await Promise.all(
        batch.map(async (link) => {
          const result = await checkExternalUrl(link.url);
          return {
            url: link.url,
            source: link.source,
            type: "external" as const,
            status: result.ok ? ("ok" as const) : ("broken" as const),
            statusCode: result.statusCode,
            error: result.error,
          };
        }),
      );
      allResults.push(...checks);
      setProgress((p) => ({ ...p, checked: p.checked + batch.length }));
      setResults([...allResults]);
    }

    setResults(allResults);
    setIsScanning(false);
    setScanComplete(true);
  }, [editCtx, siteId, templateType]);

  const brokenLinks = results.filter((r) => r.status !== "ok");
  const okLinks = results.filter((r) => r.status === "ok");

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
              <Link2Off className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-sm text-gray-800" style={{ fontWeight: 700 }}>
                {"\u05D1\u05D3\u05D9\u05E7\u05EA \u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05E9\u05D1\u05D5\u05E8\u05D9\u05DD"}
              </h2>
              <p className="text-[11px] text-gray-400">
                {"\u05E1\u05E8\u05D9\u05E7\u05EA \u05E0\u05D9\u05D5\u05D5\u05D8 \u05D5\u05EA\u05D5\u05DB\u05DF \u05DC\u05D0\u05D9\u05EA\u05D5\u05E8 \u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05DE\u05EA\u05D9\u05DD"}
              </p>
            </div>
          </div>
          <button
            type="button"
            className={`${btnReset} p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100`}
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Scan button */}
          {!isScanning && !scanComplete && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-sm text-gray-500 mb-4">
                {"\u05DC\u05D7\u05E6\u05D5 \u05DC\u05D4\u05EA\u05D7\u05D9\u05DC \u05E1\u05E8\u05D9\u05E7\u05D4 \u05E9\u05DC \u05DB\u05DC \u05D4\u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05D1\u05D0\u05EA\u05E8"}
              </p>
              <button
                type="button"
                className={`${btnReset} inline-flex items-center gap-2 px-5 py-2.5 bg-zap text-white rounded-xl text-sm hover:bg-zap-navy`}
                style={{ fontWeight: 600 }}
                onClick={runScan}
              >
                <RefreshCw className="w-4 h-4" />
                {"\u05D4\u05EA\u05D7\u05DC \u05E1\u05E8\u05D9\u05E7\u05D4"}
              </button>
            </div>
          )}

          {/* Scanning progress */}
          {isScanning && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-zap animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-2" style={{ fontWeight: 500 }}>
                {"\u05E1\u05D5\u05E8\u05E7 \u05DC\u05D9\u05E0\u05E7\u05D9\u05DD..."}
              </p>
              <p className="text-xs text-gray-400">
                {progress.checked} / {progress.total} {"\u05E0\u05D1\u05D3\u05E7\u05D5"}
              </p>
              {progress.total > 0 && (
                <div className="w-48 h-1.5 bg-gray-100 rounded-full mx-auto mt-3 overflow-hidden">
                  <div
                    className="h-full bg-zap rounded-full"
                    style={{
                      width: `${Math.round((progress.checked / progress.total) * 100)}%`,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {scanComplete && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                {brokenLinks.length === 0 ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                    <div>
                      <p className="text-sm text-green-700" style={{ fontWeight: 600 }}>
                        {"\u05DB\u05DC \u05D4\u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05EA\u05E7\u05D9\u05E0\u05D9\u05DD!"}
                      </p>
                      <p className="text-xs text-gray-400">
                        {results.length} {"\u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05E0\u05D1\u05D3\u05E7\u05D5 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4"}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                    <div>
                      <p className="text-sm text-red-700" style={{ fontWeight: 600 }}>
                        {"\u05E0\u05DE\u05E6\u05D0\u05D5 "}{brokenLinks.length}{" \u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05E9\u05D1\u05D5\u05E8\u05D9\u05DD"}
                      </p>
                      <p className="text-xs text-gray-400">
                        {okLinks.length}{" \u05EA\u05E7\u05D9\u05E0\u05D9\u05DD \u2022 "}{brokenLinks.length}{" \u05E9\u05D1\u05D5\u05E8\u05D9\u05DD"}
                      </p>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  className={`${btnReset} mr-auto p-2 rounded-lg text-gray-400 hover:text-zap hover:bg-zap-pale`}
                  onClick={runScan}
                  title={"\u05E1\u05E8\u05D5\u05E7 \u05E9\u05D5\u05D1"}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {/* Broken links list */}
              {brokenLinks.length > 0 && (
                <div>
                  <h3 className="text-xs text-red-600 mb-2" style={{ fontWeight: 600 }}>
                    <AlertTriangle className="w-3 h-3 inline ml-1" />
                    {"\u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05E9\u05D1\u05D5\u05E8\u05D9\u05DD"}
                  </h3>
                  <div className="space-y-2">
                    {brokenLinks.map((link, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-lg border border-red-100 bg-red-50/50"
                      >
                        <div className="flex items-start gap-2">
                          {link.type === "external" ? (
                            <ExternalLink className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                          ) : (
                            <FileQuestion className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-red-700 truncate" style={{ fontWeight: 600 }}>
                              {link.url}
                            </p>
                            <p className="text-[10px] text-red-400 mt-0.5">
                              {link.source}
                              {link.statusCode && ` \u2022 ${link.statusCode}`}
                              {link.error && ` \u2022 ${link.error}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* OK links (collapsed) */}
              {okLinks.length > 0 && (
                <details className="group">
                  <summary className="text-xs text-green-600 cursor-pointer select-none" style={{ fontWeight: 600 }}>
                    <CheckCircle2 className="w-3 h-3 inline ml-1" />
                    {okLinks.length}{" \u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05EA\u05E7\u05D9\u05E0\u05D9\u05DD"}
                  </summary>
                  <div className="mt-2 space-y-1">
                    {okLinks.map((link, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50/50 text-xs text-green-700"
                      >
                        <CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" />
                        <span className="truncate">{link.url}</span>
                        <span className="text-[10px] text-green-400 mr-auto">{link.source}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* No links found */}
              {results.length === 0 && (
                <div className="text-center py-6 text-gray-400 text-sm">
                  {"\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05DC\u05D9\u05E0\u05E7\u05D9\u05DD \u05DC\u05D1\u05D3\u05D9\u05E7\u05D4"}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}